/**
 * Shared style tokens for everything that sits inside the app after
 * Welcome: the compact pinned header (used by Dashboard/Task/Chat/
 * Calendar via <AppHeader />), and the plain content shell those pages
 * render into. The Welcome page also imports the pill/avatar pieces so
 * its big centered nav looks like the same control, just larger.
 */
const appShellStyles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Itim', cursive",
    background: "var(--bg-page)",
  },

  /* --- compact pinned header (Dashboard/Task/Chat/Calendar) --- */
  navBar: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    width: "100%",
    boxSizing: "border-box",
    flexWrap: "wrap",
    padding: "12px 32px",
    background: "var(--bg-surface)",
    borderBottom: "2px solid var(--ink)",
  },

  /* --- pill nav: shared by the compact header and the big Welcome pill --- */
  pillNav: {
    display: "flex",
    alignItems: "center",
    background: "var(--bg-surface)",
    border: "2.5px solid var(--ink)",
    borderRadius: 999,
    padding: 7,
    gap: 6,
    boxShadow: "4px 4px 0 0 var(--ink)",
  },
  pillNavCompact: {
    padding: 3,
    border: "2px solid var(--ink)",
    boxShadow: "3px 3px 0 0 var(--ink)",
  },
  pillNavItem: {
    fontFamily: "'Itim', cursive",
    fontSize: 18,
    color: "var(--ink-soft)",
    background: "transparent",
    border: "none",
    borderRadius: 999,
    padding: "13px 28px",
    cursor: "pointer",
  },
  pillNavItemCompact: {
    fontSize: 15,
    padding: "9px 18px",
  },
  pillNavItemActive: {
    background: "var(--bg-page)",
    color: "var(--ink)",
    fontWeight: 700,
  },

  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "var(--bg-surface)",
    border: "2px solid var(--ink)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  bellWrap: {
    position: "relative",
  },
  bellBadge: {
    position: "absolute",
    top: -4,
    right: -4,
    minWidth: 16,
    height: 16,
    borderRadius: 999,
    background: "#E0473E",
    border: "1.5px solid #FFFDF8",
    color: "#FFFDF8",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 3px",
  },
  bellDropdown: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    width: 320,
    maxHeight: 440,
    overflowY: "auto",
    background: "var(--bg-surface)",
    border: "2px solid var(--ink)",
    borderRadius: 12,
    boxShadow: "4px 4px 0 0 var(--ink)",
    padding: 14,
    boxSizing: "border-box",
    zIndex: 20,
  },
  bellDropdownTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "var(--ink)",
    margin: "0 0 10px",
  },
  bellEmptyText: {
    fontSize: 13,
    color: "var(--ink-soft)",
    margin: 0,
  },
  bellRequestList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  bellRequestRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  bellRequestName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--ink)",
    margin: 0,
  },
  bellRequestMeta: {
    fontSize: 12,
    color: "var(--ink-soft)",
    margin: "2px 0 0",
  },
  bellRequestActions: {
    display: "flex",
    gap: 6,
    flexShrink: 0,
  },
  bellAcceptBtn: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "var(--accent-color)",
    color: "#FFFDF8",
    border: "2px solid var(--ink)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  },
  bellRejectBtn: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "var(--bg-surface)",
    color: "var(--ink)",
    border: "2px solid var(--ink)",
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1,
  },
  bellDivider: {
    borderTop: "1.5px dashed #E7E2D6",
    margin: "14px 0 12px",
  },
  bellNotifHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  bellMarkAllBtn: {
    fontSize: 11.5,
    fontWeight: 700,
    color: "var(--accent-color)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  bellNotifList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  bellNotifRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    width: "100%",
    textAlign: "left",
    background: "none",
    border: "none",
    borderRadius: 8,
    padding: "8px 6px",
    cursor: "pointer",
  },
  bellNotifRowUnread: {
    background: "#F6F3EC",
  },
  bellNotifIcon: {
    fontSize: 15,
    flexShrink: 0,
    lineHeight: "18px",
  },
  bellNotifBody: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
    flex: 1,
  },
  bellNotifTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--ink)",
    lineHeight: 1.4,
  },
  bellNotifSnippet: {
    fontSize: 12,
    color: "var(--ink-soft)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bellNotifTime: {
    fontSize: 11,
    color: "var(--ink-faint)",
    marginTop: 2,
  },
  bellNotifDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "var(--accent-color)",
    flexShrink: 0,
    marginTop: 5,
  },
  userChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "var(--accent-color)",
    border: "2px solid var(--ink)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 16,
    color: "#FFFDF8",
    flexShrink: 0,
  },
  userName: {
    fontFamily: "'Itim', cursive",
    fontSize: 15,
    fontWeight: 700,
    color: "var(--ink)",
  },
  logoutBtn: {
    fontFamily: "'Itim', cursive",
    fontSize: 14,
    color: "var(--ink)",
    background: "var(--bg-surface)",
    border: "2px solid var(--ink)",
    borderRadius: 999,
    padding: "8px 16px",
    cursor: "pointer",
  },

  /* --- plain content shell for inner pages --- */
  main: {
    width: "100%",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "40px 40px 80px",
    boxSizing: "border-box",
  },
  section: {
    marginBottom: 56,
  },
  sectionTitle: {
    fontFamily: "'Itim', cursive",
    fontWeight: 700,
    fontSize: "clamp(22px, 2.6vw, 28px)",
    color: "var(--ink)",
    margin: "0 0 20px",
  },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 20,
  },
  actionCard: {
    border: "2px solid var(--ink)",
    padding: "26px 22px",
    boxSizing: "border-box",
  },
  actionIconWrap: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "var(--bg-page)",
    border: "2px solid var(--ink)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
  },
  actionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "var(--ink)",
    margin: "0 0 6px",
  },
  actionDesc: {
    fontSize: 14,
    color: "var(--ink-soft)",
    lineHeight: 1.55,
    margin: 0,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "48px 24px",
    border: "2.5px dashed var(--ink)",
    boxSizing: "border-box",
  },
  emptyStateText: {
    fontSize: 16,
    color: "var(--ink-soft)",
    margin: 0,
    maxWidth: 380,
    lineHeight: 1.6,
  },
  emptyStateBtn: {
    background: "var(--accent-color)",
    color: "#FFFDF8",
    fontSize: 16,
    padding: "12px 28px",
    marginTop: 20,
  },
};

export default appShellStyles;
